/**
 * Application Providers
 *
 * Combines all context providers for the application.
 * Order matters: AuthProvider → TenantProvider → QueryProvider → Theme
 *
 * Includes session refresh logic to keep cookie synced with OIDC auth.
 * Includes CJIS 5.6.3.1 idle timeout (30-min lock).
 * Includes PostHog analytics initialization.
 */

'use client';

import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import { Theme } from '@radix-ui/themes';
import posthog from 'posthog-js';
import { TenantProvider } from '@/lib/tenant-context';
import { AuthProvider } from '@/lib/auth-context';
import { FeatureFlagProvider } from '@/lib/feature-flag-context';
import { OntologyProvider } from '@/components/evidence/shared/ontology-context';
import { getAuthClient } from '@/lib/auth-client';
import { isSessionCapExceeded, SESSION_LOCK_BROADCAST, SESSION_LOCK_STORAGE_KEY } from '@/lib/auth-client-gcip';
import { isInitialResetRecoveryLocation, isLoggingOut, registerAuthResetHandlers, resetAuthSession, wasSessionRecentlyEstablished } from '@/lib/auth-reset';
import { isPublicRoute } from '@/lib/auth-routes';
import {
  emitSessionEnd,
  markSessionStart,
  registerSessionTelemetry,
} from '@/lib/telemetry/session-telemetry';
import { useIdleTimeout } from '@/hooks/useIdleTimeout';
import { onUploadActivity } from '@/lib/upload/upload-activity';
import { spanPlayback } from '@/lib/ask-foltrigg/span-playback';
import { SessionLockOverlay } from '@/components/SessionLockOverlay';
import { SessionTimeoutWarning } from '@/components/SessionTimeoutWarning';
import { logger } from '@/lib/logger';

// Pre-lock warning window. Must match useIdleTimeout's warningMs option.
const SESSION_WARNING_MS = 60 * 1000;
// Radix Themes CSS is imported in globals.css (before Tailwind) for proper CSS cascade

// Initialize PostHog analytics (only in browser, only if key is configured)
if (typeof window !== 'undefined' && process.env.NEXT_PUBLIC_POSTHOG_KEY) {
  posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY, {
    api_host: process.env.NEXT_PUBLIC_POSTHOG_HOST || 'https://us.i.posthog.com',
    person_profiles: 'identified_only',
    capture_pageview: true,
    capture_pageleave: true,
    capture_exceptions: true, // Enable automatic exception capture for error tracking
    loaded: (posthog) => {
      if (process.env.NODE_ENV === 'development') {
        // Disable in development to avoid noise
        posthog.opt_out_capturing();
      }
      logger.debug('[PostHog] Initialized', { host: process.env.NEXT_PUBLIC_POSTHOG_HOST });
    },
  });
}

// Create Query Client with default options
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000, // 1 minute
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

registerAuthResetHandlers({
  clearQueryCache: () => queryClient.clear(),
});

export function Providers({ children }: { children: ReactNode }) {
  const [showLockOverlay, setShowLockOverlay] = useState(false);
  // Gate the idle timer on authentication. Running it on /login,
  // /auth/callback, or /logout is wrong: there is no session to lock,
  // and a slow IdP roundtrip / MFA prompt / account picker that keeps
  // the user on the unauthenticated page for ~28-29 min would let the
  // 30-min timer fire seconds-to-minutes after the dashboard finally
  // renders — none of mousedown/keydown/touchstart/scroll fire during
  // a redirect-driven login, so the existing timer never resets at the
  // auth transition. Toggling enabled false→true on auth restarts the
  // timer cleanly via useIdleTimeout's effect re-run.
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const { isIdle, isWarning, markActive } = useIdleTimeout({
    enabled: isAuthenticated,
    warningMs: SESSION_WARNING_MS,
  });
  const lockedRef = useRef(false);
  // Mirror isIdle into a ref so the session-refresh callback can read the
  // current value without re-establishing the auth subscription on every
  // idle <-> active transition (which churned setIsAuthenticated and reset
  // the idle timer mid-session).
  const isIdleRef = useRef(false);
  useEffect(() => {
    isIdleRef.current = isIdle;
  }, [isIdle]);

  // gh #876: register the session-end telemetry sink once. Provides the
  // PostHog capture fn (posthog is opt_out'd in dev — see init above — so the
  // module's dev console.debug sink is the only dev signal) and an isIdle
  // getter so emits from lib/auth-reset.ts (which has no view of the idle
  // timer) still record was_idle correctly. registerSessionTelemetry is
  // idempotent; re-running on mount is harmless.
  useEffect(() => {
    registerSessionTelemetry({
      capture: (event, properties) => {
        if (typeof window !== 'undefined' && process.env.NEXT_PUBLIC_POSTHOG_KEY) {
          posthog.capture(event, properties);
        }
      },
      isIdle: () => isIdleRef.current,
    });
  }, []);

  // Session refresh logic: Keep cookie synced with auth provider.
  // CJIS 5.6.3.1: stop refreshing when user is idle so IdP session expires.
  useEffect(() => {
    if (isInitialResetRecoveryLocation()) {
      logger.warn('[Providers] Skipping auth bootstrap during reset recovery');
      return;
    }

    const client = getAuthClient();
    let intervalId: NodeJS.Timeout | null = null;
    let refreshing = false;
    let hadUser = false;

    const unsubscribe = client.onAuthStateChanged((user) => {
      setIsAuthenticated(!!user);
      if (intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }

      if (user) {
        // gh #876: record session start on the authenticated→user transition
        // so session_end telemetry can report session_age_s. Only on a fresh
        // session (not on every token-refresh re-notify), so age reflects
        // login time, not the last silent refresh.
        if (!hadUser) {
          markSessionStart();
          // gh #1007: a fresh authenticated session invalidates any stale
          // cross-tab idle-lock stamp. Clearing it here (localStorage is
          // origin-shared) stops a leftover stamp from an earlier locked
          // session from re-raising the lock overlay on this tab's next
          // visibilitychange right after the user re-authenticated.
          if (typeof window !== 'undefined') {
            try {
              window.localStorage.removeItem(SESSION_LOCK_STORAGE_KEY);
            } catch {
              // localStorage unavailable; nothing to clear.
            }
          }
        }
        hadUser = true;

        if (typeof window !== 'undefined' && process.env.NEXT_PUBLIC_POSTHOG_KEY) {
          posthog.identify(user.uid, {
            email: user.email,
            name: user.displayName,
          });
        }

        const refreshSession = async () => {
          if (refreshing || isIdleRef.current) {
            return;
          }
          refreshing = true;

          try {
            logger.debug('[Providers] Refreshing session cookie...');

            const token = await client.getIdToken(true);
            // CJIS IA-11: if the absolute cap was reached, getIdToken returns
            // null or a capped token. Either way, drop the session and send
            // the user to the IdP for a fresh interactive authentication.
            if (!token || isSessionCapExceeded(token)) {
              logger.warn(
                '[Providers] Session cap reached or token unavailable; signing out'
              );
              // gh #876: 12h IA-11 cap hit during the background refresh.
              emitSessionEnd('session-cap');
              if (intervalId) {
                clearInterval(intervalId);
                intervalId = null;
              }
              try {
                await client.signOut();
              } catch (signOutErr) {
                logger.error('[Providers] signOut failed after cap', signOutErr);
                if (typeof window !== 'undefined') {
                  // signOut itself failed; fall back to a hard reload so no
                  // client auth state survives.
                  // eslint-disable-next-line @next/next/no-location-assign-relative-destination -- last-resort hard reload when signOut fails
                  window.location.href = '/login';
                }
              }
              return;
            }

            const response = await fetch('/api/auth/session', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ token })
            });

            if (response.ok) {
              logger.debug('[Providers] Session cookie refreshed successfully');
            } else {
              logger.error('[Providers] Failed to refresh session cookie:', response.status);
            }
          } catch (error) {
              logger.error('[Providers] Error refreshing session:', error);
          } finally {
            refreshing = false;
          }
        };

        // Don't refresh immediately -- the OIDC callback already set the cookie.
        // getIdToken(true) triggers signinSilent → userLoaded → onAuthStateChanged,
        // which re-enters this callback and creates an infinite loop.
        intervalId = setInterval(refreshSession, 50 * 60 * 1000);
      } else {
        if (typeof window !== 'undefined' && process.env.NEXT_PUBLIC_POSTHOG_KEY) {
          posthog.reset();
        }
        // Only act on a real authenticated → null transition (user went from
        // authenticated → null), not on initial mount where OIDC client
        // hasn't initialized yet. Acting on initial null causes a race:
        // callback page loads → Providers fires null → DELETE cookie →
        // handleCallback sets cookie → flicker.
        if (hadUser) {
          // #1619: the session ended (logout, IA-11 cap, or unexpected expiry).
          // Stop span audio before any redirect so it never carries into the
          // login screen or a signed-out tab.
          spanPlayback.stop();
          const path =
            typeof window !== 'undefined' ? window.location.pathname : '';
          const onProtectedRoute = !!path && !isPublicRoute(path);
          // signOut() and lockSession() both call setLoggingOut(true); the
          // idle lock also drives isIdle/lockedRef. Either means the teardown
          // + UX is already owned elsewhere (logout navigates to /logout; the
          // idle lock keeps its overlay up), so we must NOT redirect here.
          const deliberateOrIdleLock =
            isLoggingOut() || isIdleRef.current || lockedRef.current;

          if (!deliberateOrIdleLock && onProtectedRoute) {
            // gh #876 (complaint 2): the session went null UNEXPECTEDLY on a
            // protected route — 12h IA-11 cap reached during a silent Firebase
            // token refresh, a lost sign_in_second_factor claim, or a
            // token-read failure surfaced by onIdTokenChanged. None of these
            // ride a failing fetch, so api-client's 401 path never fires and
            // the page would sit dead until a manual refresh. Redirect to
            // /login via resetAuthSession, which clears the __session cookie
            // (purge=cookie), drops caches, and navigates. Its gh #946
            // freshness guard suppresses this if a session was just
            // (re)established, so no bounce loop. The session_end telemetry
            // event is emitted inside resetAuthSession (reason
            // 'session-expired'); no separate emit here.
            logger.warn('[Providers] Session ended unexpectedly - redirecting to login');
            queryClient.clear();
            void resetAuthSession('session-expired');
          } else {
            void fetch('/api/auth/session', { method: 'DELETE' }).catch((error) => {
              logger.warn('[Providers] Failed to clear session cookie after client sign-out', error);
            });
            queryClient.clear();
            logger.debug('[Providers] User signed out - session cleared');
          }
        }
      }
    });

    return () => {
      unsubscribe();
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, []);

  // Treat an actively-progressing upload as user activity so the CJIS
  // idle lock does not fire mid-transfer and detonate an unattended
  // upload (gh #877, CJIS_EXCEPTIONS.md PE-27). markActive() no-ops when
  // the timer is disabled (unauthenticated), and the upload layer only
  // pulses while bytes are actively moving (throttled), so a stalled or
  // finished batch resumes the normal countdown and still locks on time.
  useEffect(() => {
    return onUploadActivity(() => markActive());
  }, [markActive]);

  // Show lock overlay + server-side lock on idle (CJIS AC-11 / SC-10).
  // The server-side lock (bearer blocklist + cookie clear + force-prompt
  // flag) is what makes this a real session lock rather than a UI-only
  // screen. Fires exactly once per idle transition via lockedRef guard.
  useEffect(() => {
    if (!isIdle) {
      lockedRef.current = false;
      return;
    }
    // eslint-disable-next-line react-hooks/set-state-in-effect -- CJIS AC-11/SC-10 idle-lock transition; UI flag must flip in sync with the lockedRef guard below
    setShowLockOverlay(true);
    // #1619 / CJIS 5.6.3.1: silence any cited-span audio the moment the screen
    // locks. The span player is an app-wide singleton with no view of the idle
    // timer, so call audio can otherwise stay audible behind the lock overlay.
    // Idempotent, so running it on every idle transition (not just the first)
    // is safe.
    spanPlayback.stop();
    if (lockedRef.current) return;
    lockedRef.current = true;
    // gh #876: CJIS 5.6.3.1 / AC-11 idle lock — the most likely source of
    // "logged out mid-use" (a 30-min read/listen with no click/scroll). Emit
    // once per idle transition (guarded by lockedRef above).
    emitSessionEnd('idle-lock', { wasIdle: true });
    const client = getAuthClient();
    client.lockSession().catch((e) => {
      logger.warn('[Providers] lockSession failed', e);
    });
  }, [isIdle]);

  // Cross-tab lock propagation: if any tab of this browser session locks,
  // all other tabs show the overlay too. lockSession() only runs in the
  // originating tab — listeners just surface the UI.
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    // Raise the lock overlay in response to a sibling tab's lockSession().
    // Shared by all three delivery paths (BroadcastChannel, storage event,
    // visibilitychange re-read) so the freshness guard is applied uniformly.
    const raiseSiblingLock = (source: string) => {
      // gh #946: ignore a cross-tab lock if THIS tab just (re)established a
      // session. A still-idle background tab that fired its 30-min lock right
      // after the user re-authenticated here would otherwise flash the lock
      // overlay onto (and mark locked) a freshly-authenticated tab — part of
      // the post-idle-lock bounce loop. The freshness marker self-expires
      // (<=60s), so a genuine later cross-tab lock still surfaces normally.
      if (wasSessionRecentlyEstablished()) {
        logger.warn(`[Providers] Ignoring cross-tab lock (${source}) — session recently established`);
        return;
      }
      setShowLockOverlay(true);
      lockedRef.current = true;
      // #1619: a lock raised by a sibling tab must silence this tab's span
      // audio too, or a locked screen keeps playing call audio.
      spanPlayback.stop();
    };

    // gh #1007: a frozen/discarded background tab never receives the
    // BroadcastChannel 'lock' message, so on refocus it would sit in a
    // half-authenticated ("?" avatar) state after a sibling tab tore the
    // shared session down. lockSession() also stamps localStorage; re-read it
    // whenever this tab becomes visible so the lock is applied deterministically
    // regardless of whether the live channel message was delivered.
    const hasPendingLockStamp = (): boolean => {
      try {
        return !!window.localStorage.getItem(SESSION_LOCK_STORAGE_KEY);
      } catch {
        return false;
      }
    };

    let bc: BroadcastChannel | null = null;
    if ('BroadcastChannel' in window) {
      try {
        bc = new BroadcastChannel(SESSION_LOCK_BROADCAST);
        bc.onmessage = (ev) => {
          if (ev.data?.type === 'lock') {
            raiseSiblingLock('broadcast');
          }
        };
      } catch {
        // BroadcastChannel unavailable; storage-event + visibility paths remain.
      }
    }

    // storage events fire in OTHER tabs when localStorage changes. This catches
    // a sibling lock even when BroadcastChannel is unavailable or the message
    // was dropped, provided this tab is alive to receive it.
    const onStorage = (ev: StorageEvent) => {
      if (ev.key === SESSION_LOCK_STORAGE_KEY && ev.newValue) {
        raiseSiblingLock('storage-event');
      }
    };
    window.addEventListener('storage', onStorage);

    // The frozen-tab backstop: on regaining visibility, reconcile against the
    // localStorage stamp that survives background freezing.
    const onVisibility = () => {
      if (document.visibilityState === 'visible' && !lockedRef.current && hasPendingLockStamp()) {
        raiseSiblingLock('visibilitychange');
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      bc?.close();
      window.removeEventListener('storage', onStorage);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  const handleResume = () => {
    // Resume is handled by SessionLockOverlay → client.signIn() (full IdP
    // redirect). Kept for API compatibility; clearing state here matters
    // only if signIn() fails to navigate (shouldn't happen in practice).
    markActive();
    lockedRef.current = false;
    setShowLockOverlay(false);
  };

  // Best-effort proactive session refresh. Mirrors the 50-min interval's
  // token + __session cookie renewal so an explicit "Stay Logged In" actually
  // extends the authenticated session instead of only postponing the local
  // idle lock. _suppressNotify inside getIdToken(true) prevents the
  // signinSilent -> userLoaded -> onAuthStateChanged re-entry loop, so this is
  // safe to call outside the interval.
  const refreshSessionNow = useCallback(async () => {
    try {
      const client = getAuthClient();
      const token = await client.getIdToken(true);
      // CJIS IA-11: never re-up the cookie past the absolute cap. If the token
      // is gone or capped, leave it to the interval/middleware to route the
      // user to interactive login.
      if (!token || isSessionCapExceeded(token)) {
        logger.warn('[Providers] Stay-logged-in refresh: no token or cap reached');
        return;
      }
      const response = await fetch('/api/auth/session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      });
      if (response.ok) {
        logger.debug('[Providers] Stay-logged-in session refreshed');
      } else {
        logger.error('[Providers] Stay-logged-in cookie refresh failed:', response.status);
      }
    } catch (error) {
      logger.error('[Providers] Stay-logged-in refresh error:', error);
    }
  }, []);

  const handleStayLoggedIn = () => {
    // User dismissed the pre-lock warning. Reset the idle timer so the lock is
    // not fired, AND proactively refresh the session token + __session cookie.
    // markActive() alone only postpones the client-side idle lock; without the
    // refresh, a user who clicks "Stay Logged In" and then stays inactive can
    // still have the cookie lapse at its fixed 55-min lifetime (the background
    // refresh is a 50-min login-anchored, idle-gated interval) and get bounced
    // to the IdP on their next navigation. No server-side state to clear
    // (lockSession() has not run yet; the warning fires before isIdle).
    markActive();
    void refreshSessionNow();
  };

  return (
    <Theme>
      <AuthProvider>
        <TenantProvider>
          <QueryClientProvider client={queryClient}>
            <FeatureFlagProvider>
              <OntologyProvider>
                {children}
              </OntologyProvider>
            </FeatureFlagProvider>
            {showLockOverlay && <SessionLockOverlay onResume={handleResume} />}
            {isWarning && !showLockOverlay && (
              <SessionTimeoutWarning
                remainingMs={SESSION_WARNING_MS}
                onStay={handleStayLoggedIn}
              />
            )}
            <Toaster
              position="top-right"
              toastOptions={{
                duration: 4000,
                style: {
                  background: '#363636',
                  color: '#fff',
                },
                success: {
                  duration: 3000,
                  iconTheme: {
                    primary: '#4ade80',
                    secondary: '#fff',
                  },
                },
                error: {
                  duration: 5000,
                  iconTheme: {
                    primary: '#ef4444',
                    secondary: '#fff',
                  },
                },
              }}
            />
          </QueryClientProvider>
        </TenantProvider>
      </AuthProvider>
    </Theme>
  );
}
